/**
 * API helper for netx-lists endpoints.
 * Base: API_BASE + '/lists'  (maps to /api/v1 on the backend).
 */
import { apiFetch } from 'api/client';

const BASE = () => '/lists';

// ── Stats ──────────────────────────────────────────────────────────
export const fetchStats = () =>
    apiFetch(`${BASE()}/stats`).then(r => r.json());

// ── Health ─────────────────────────────────────────────────────────
export const fetchHealth = () =>
    apiFetch(`${BASE()}/health`).then(r => r.json());

// ── Categories ─────────────────────────────────────────────────────
export const fetchCategories = () =>
    apiFetch(`${BASE()}/categories`).then(r => r.json());

export const fetchCategory = (id) =>
    apiFetch(`${BASE()}/categories/${id}`).then(r => r.json());

export const createCategory = (body) =>
    apiFetch(`${BASE()}/categories`, { method: 'POST', json: body }).then(r => r.json());

export const updateCategory = (id, body) =>
    apiFetch(`${BASE()}/categories/${id}`, { method: 'PUT', json: body }).then(r => r.json());

export const deleteCategory = (id) =>
    apiFetch(`${BASE()}/categories/${id}`, { method: 'DELETE' });

// ── Providers ──────────────────────────────────────────────────────
export const fetchProviders = () =>
    apiFetch(`${BASE()}/providers`).then(r => r.json());

export const fetchProvider = (id) =>
    apiFetch(`${BASE()}/providers/${id}`).then(r => r.json());

export const createProvider = (body) =>
    apiFetch(`${BASE()}/providers`, { method: 'POST', json: body }).then(r => r.json());

export const updateProvider = (id, body) =>
    apiFetch(`${BASE()}/providers/${id}`, { method: 'PUT', json: body }).then(r => r.json());

export const toggleProvider = (id) =>
    apiFetch(`${BASE()}/providers/${id}/toggle`, { method: 'POST' }).then(r => r.json());

export const deleteProvider = (id) =>
    apiFetch(`${BASE()}/providers/${id}`, { method: 'DELETE' });

// ── IP Lists ───────────────────────────────────────────────────────
export const fetchIpLists = (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.page_size) qs.set('page_size', params.page_size);
    if (params.search) qs.set('search', params.search);
    if (params.category_id) qs.set('category_id', params.category_id);
    if (params.provider_id) qs.set('provider_id', params.provider_id);
    if (params.is_active !== undefined && params.is_active !== '') qs.set('is_active', params.is_active);
    if (params.external_owner_id) qs.set('external_owner_id', params.external_owner_id);
    return apiFetch(`${BASE()}/ip-lists?${qs}`).then(r => r.json());
};

export const searchIp = async (ip) => {
    const res = await apiFetch(`${BASE()}/ip-lists/search?ip=${encodeURIComponent(ip)}`);
    const json = await res.json();
    console.debug('[listsApi] searchIp raw response:', json);
    // Normalise: API may return a plain array, { data: [...] }, { lists: [...] },
    // or an array of match-wrapper objects like { ip_list: {...}, matched_entry: "..." }.
    const arr = Array.isArray(json) ? json
        : Array.isArray(json.data) ? json.data
        : Array.isArray(json.lists) ? json.lists
        : Array.isArray(json.matches) ? json.matches
        : [];

    return arr.map(item => {
        if (!item || typeof item !== 'object') return item;

        // Case 1: match-wrapper with a nested ip_list / list sub-object
        if (!item.name && (item.ip_list || item.list)) {
            const list = item.ip_list || item.list;
            return { ...list, _matched_entry: item.matched_entry || item.ip_address || item.match };
        }

        // Case 2: IPEntry object (has ip_list_id + ip_address but no list name/id at top level)
        // The id here is the entry id, not the list id — use ip_list_id for navigation.
        if (item.ip_list_id && item.ip_address && !item.name) {
            return {
                id: item.ip_list_id,
                _matched_entry: item.ip_address,
                // Carry through any joined list fields the API may include
                ...item.ip_list,
            };
        }

        // Case 3: Plain IPList object — pass through as-is
        return item;
    });
};

export const fetchIpList = (id) =>
    apiFetch(`${BASE()}/ip-lists/${id}`).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
    });

export const fetchIpListEntries = (id, params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.page_size) qs.set('page_size', params.page_size);
    return apiFetch(`${BASE()}/ip-lists/${id}/entries?${qs}`).then(r => r.json());
};

export const exportIpList = (id, format = 'json') =>
    apiFetch(`${BASE()}/ip-lists/${id}/export?format=${format}`);

export const createIpList = (body) =>
    apiFetch(`${BASE()}/ip-lists`, { method: 'POST', json: body }).then(r => r.json());

export const updateIpList = (id, body) =>
    apiFetch(`${BASE()}/ip-lists/${id}`, { method: 'PUT', json: body }).then(r => r.json());

export const deleteIpList = (id) =>
    apiFetch(`${BASE()}/ip-lists/${id}`, { method: 'DELETE' });

export const addIpEntry = (listId, body) =>
    apiFetch(`${BASE()}/ip-lists/${listId}/entries`, { method: 'POST', json: body }).then(r => r.json());

export const bulkAddIpEntries = (listId, entries) =>
    apiFetch(`${BASE()}/ip-lists/${listId}/entries/bulk`, { method: 'POST', json: { entries } }).then(r => r.json());

export const deleteIpEntry = (listId, entryId) =>
    apiFetch(`${BASE()}/ip-lists/${listId}/entries/${entryId}`, { method: 'DELETE' });

// ── Sync ───────────────────────────────────────────────────────────
export const fetchSyncLogs = (limit = 50) =>
    apiFetch(`${BASE()}/sync/logs?limit=${limit}`).then(r => r.json());

export const fetchSyncStatus = () =>
    apiFetch(`${BASE()}/sync/status`).then(r => r.json());

export const fetchProviderSyncLogs = (providerId, limit = 20) =>
    apiFetch(`${BASE()}/sync/provider/${providerId}/logs?limit=${limit}`).then(r => r.json());

export const triggerSyncAll = () =>
    apiFetch(`${BASE()}/sync/all`, { method: 'POST' }).then(r => r.json());

export const triggerSyncProvider = (providerId) =>
    apiFetch(`${BASE()}/sync/provider/${providerId}`, { method: 'POST' }).then(r => r.json());
